import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    IAccount, IAccountCreate, IAccountUpdate, IAccountingOverview, IBalanceSheet, IClosingStatus, IJournalEntry, ILedger,
    IManualEntry, IMoneyMovement, IOpeningBalances, IPage, IPeriodClose, IYearClose, IYearClosingStatus, IProfitLoss, IRetryResult, ITransfer, ITrialBalance, JournalSource,
} from '../interfaces/accounting.interface';

const auth = () => ({ headers: AuthService.getAuthHeader() });
const E = ENDPOINTS.ACCOUNTING;

export interface IEntryFilter {
    from?: string;
    to?: string;
    source?: JournalSource | '';
    accountId?: string;
    page?: number;
    size?: number;
}

export const AccountingService = {
    listAccounts: async (institutionId: string): Promise<IAccount[]> =>
        (await axios.get(E.ACCOUNTS(institutionId), auth())).data,

    createAccount: async (institutionId: string, body: IAccountCreate): Promise<IAccount> =>
        (await axios.post(E.ACCOUNTS(institutionId), body, auth())).data,

    updateAccount: async (institutionId: string, accountId: string, body: IAccountUpdate): Promise<IAccount> =>
        (await axios.put(E.ACCOUNT(institutionId, accountId), body, auth())).data,

    deleteAccount: async (institutionId: string, accountId: string): Promise<void> => {
        await axios.delete(E.ACCOUNT(institutionId, accountId), auth());
    },

    listEntries: async (institutionId: string, filter: IEntryFilter = {}): Promise<IPage<IJournalEntry>> => {
        const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== '' && v !== undefined));
        return (await axios.get(E.ENTRIES(institutionId), { ...auth(), params })).data;
    },

    getEntry: async (institutionId: string, entryId: string): Promise<IJournalEntry> =>
        (await axios.get(E.ENTRY(institutionId, entryId), auth())).data,

    createEntry: async (institutionId: string, body: IManualEntry): Promise<IJournalEntry> =>
        (await axios.post(E.ENTRIES(institutionId), body, auth())).data,

    reverseEntry: async (institutionId: string, entryId: string, date: string, reason?: string): Promise<IJournalEntry> =>
        (await axios.post(E.REVERSE(institutionId, entryId), { date, reason }, auth())).data,

    moneyIn: async (institutionId: string, body: IMoneyMovement): Promise<IJournalEntry> =>
        (await axios.post(E.MONEY_IN(institutionId), body, auth())).data,

    moneyOut: async (institutionId: string, body: IMoneyMovement): Promise<IJournalEntry> =>
        (await axios.post(E.MONEY_OUT(institutionId), body, auth())).data,

    transfer: async (institutionId: string, body: ITransfer): Promise<IJournalEntry> =>
        (await axios.post(E.TRANSFER(institutionId), body, auth())).data,

    openingBalances: async (institutionId: string, body: IOpeningBalances): Promise<IJournalEntry> =>
        (await axios.post(E.OPENING_BALANCES(institutionId), body, auth())).data,

    overview: async (institutionId: string): Promise<IAccountingOverview> =>
        (await axios.get(E.OVERVIEW(institutionId), auth())).data,

    retryPosting: async (institutionId: string, kind: string, id: string): Promise<IRetryResult> =>
        (await axios.post(E.RETRY_POSTING(institutionId, kind, id), {}, auth())).data,

    trialBalance: async (institutionId: string, asOf: string): Promise<ITrialBalance> =>
        (await axios.get(E.TRIAL_BALANCE(institutionId), { ...auth(), params: { asOf } })).data,

    profitLoss: async (institutionId: string, from: string, to: string): Promise<IProfitLoss> =>
        (await axios.get(E.PROFIT_LOSS(institutionId), { ...auth(), params: { from, to } })).data,

    balanceSheet: async (institutionId: string, asOf: string): Promise<IBalanceSheet> =>
        (await axios.get(E.BALANCE_SHEET(institutionId), { ...auth(), params: { asOf } })).data,

    ledger: async (institutionId: string, accountId: string, from: string, to: string): Promise<ILedger> =>
        (await axios.get(E.LEDGER(institutionId), { ...auth(), params: { accountId, from, to } })).data,

    closingStatus: async (institutionId: string): Promise<IClosingStatus> =>
        (await axios.get(E.CLOSING(institutionId), auth())).data,

    closeMonth: async (institutionId: string, year: number, month: number): Promise<IPeriodClose> =>
        (await axios.post(E.CLOSE_MONTH(institutionId, year, month), {}, auth())).data,

    /** Closes every open month up to and including this one. */
    closeThrough: async (institutionId: string, year: number, month: number): Promise<IPeriodClose[]> =>
        (await axios.post(E.CLOSE_THROUGH(institutionId, year, month), {}, auth())).data,

    reopenMonth: async (institutionId: string, year: number, month: number, reason: string): Promise<IPeriodClose> =>
        (await axios.post(E.REOPEN_MONTH(institutionId, year, month), { reason }, auth())).data,

    yearClosingStatus: async (institutionId: string): Promise<IYearClosingStatus> =>
        (await axios.get(E.YEAR_CLOSING(institutionId), auth())).data,

    closeYear: async (institutionId: string, yearEnd: string): Promise<IYearClose> =>
        (await axios.post(E.CLOSE_YEAR(institutionId, yearEnd), {}, auth())).data,

    reopenYear: async (institutionId: string, yearEnd: string, reason: string): Promise<IYearClose> =>
        (await axios.post(E.REOPEN_YEAR(institutionId, yearEnd), { reason }, auth())).data,
};
